import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { gamingAPI } from '../services/api';
import { Timer } from 'lucide-react';
import toast from 'react-hot-toast';

const IDLE_CHOICES = [
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
];

const formatIdleClock = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const idleWindowLabel = (minutes) => {
  const row = IDLE_CHOICES.find((c) => c.minutes === Number(minutes));
  return row ? row.label : '1 hour';
};

const IdleDestroyToggle = ({ userId, session = null }) => {
  const [enabled, setEnabled] = useState(false);
  const [minutes, setMinutes] = useState(60);
  const queryClient = useQueryClient();

  const { isLoading } = useQuery(
    ['preferences', userId],
    () => gamingAPI.getPreferences(),
    {
      enabled: !!userId,
      onSuccess: (data) => {
        setEnabled(!!data.auto_destroy_idle);
        setMinutes(data.idle_destroy_minutes || 60);
      },
    }
  );

  const mutation = useMutation(
    (payload) => gamingAPI.updatePreferences(payload),
    {
      onSuccess: (data) => {
        setEnabled(!!data.auto_destroy_idle);
        setMinutes(data.idle_destroy_minutes || 60);
        toast.success(
          data.auto_destroy_idle
            ? `GPU will destroy after ${idleWindowLabel(data.idle_destroy_minutes)} idle in the Play tab`
            : 'Auto-destroy off — machine stays until you stop it'
        );
        queryClient.invalidateQueries(['preferences', userId]);
        queryClient.invalidateQueries('gamingSessions');
      },
      onError: () => toast.error('Could not update auto-destroy preference'),
    }
  );

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-600 bg-gray-800/60 p-4 animate-pulse h-20" />
    );
  }

  return (
    <div
      className={`rounded-lg p-4 border ${
        enabled
          ? 'border-amber-500/40 bg-amber-950/30'
          : 'border-gray-600 bg-gray-800/60'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-full ${enabled ? 'bg-amber-500/20' : 'bg-gray-700'}`}>
            <Timer className={`h-5 w-5 ${enabled ? 'text-amber-300' : 'text-gray-400'}`} />
          </div>
          <div>
            <h3 className="text-white font-semibold">
              Auto-destroy after {idleWindowLabel(minutes)} idle
            </h3>
            <p className="text-sm text-white/70">
              {enabled
                ? `Idle means no mouse or keyboard in the Play tab (the window that opens after Play) for ${idleWindowLabel(minutes)}. Using your PC in other tabs or apps does not count. The Game dashboard does not keep the GPU alive.`
                : 'Off by default. Turn on to stop billing when you walk away from the Play tab. Default is 1 hour of no input there — not 1 hour from launch. Playing in that tab resets the clock.'}
            </p>
            {enabled && (
              <div className="flex flex-wrap gap-2 mt-3">
                {IDLE_CHOICES.map((choice) => (
                  <button
                    key={choice.minutes}
                    type="button"
                    disabled={mutation.isLoading}
                    onClick={() =>
                      mutation.mutate({ idle_destroy_minutes: choice.minutes, auto_destroy_idle: true })
                    }
                    className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                      minutes === choice.minutes
                        ? 'bg-amber-400 text-amber-950 border-amber-300'
                        : 'bg-gray-800 text-white/80 border-white/15 hover:border-amber-400/60'
                    }`}
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
            )}
            {enabled && session && ['pending', 'starting', 'running'].includes(session.status) && (
              <p className="text-sm text-amber-200 mt-2">
                Idle now {formatIdleClock(session.idle_seconds)} · destroys in{' '}
                {formatIdleClock(
                  session.idle_remaining_seconds != null
                    ? session.idle_remaining_seconds
                    : minutes * 60
                )}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() =>
            mutation.mutate({
              auto_destroy_idle: !enabled,
              idle_destroy_minutes: minutes || 60,
            })
          }
          disabled={mutation.isLoading}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
            enabled ? 'bg-amber-500' : 'bg-gray-600'
          } ${mutation.isLoading ? 'opacity-50' : ''}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default IdleDestroyToggle;
